import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { HStack, Text } from '@chakra-ui/react'
import { WarningTwoIcon } from '@editor/assets/icons'
import { EDITOR_TOOLS, MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER, MAX_EDITOR_LAYERS } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import { PointArray } from '@editor/interfaces/attribute'
import { AnchorColors, CylinderMeshProps, LineStyle } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra } from '@editor/interfaces/editor'
import { InspectionItem } from '@editor/interfaces/interfaces'
import { Polygon } from '@editor/interfaces/shape'
import { isElementHovered } from '@editor/pages/projects/editor/infoPanels/utils'
import {
  selectIsAdjustingLabelColors,
  selectIsAdjustingUnlabeledColors,
  selectisGroupColoring,
} from '@editor/pages/projects/editor/store/selectors'
import { getContrastingOutlineColor } from '@editor/services/Color'
import { toInspectionGroups } from '@editor/services/InspectionGroup'
import { isCylinderWithinBoundingBox } from '@editor/services/Shape'
import { calculateRebarSurfaceArea, getDistanceLabel, zeroPad } from '@editor/services/Util'
import { getVolumeBoundingBox, getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { setWorkingElementId } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { CircleAnchorIcon } from '@infraloop/common'
import { Box3, Vector3 } from 'three'

import {
  DistanceLabelWorkingCylinderProps,
  addWorkingLines,
  completeLastWorkingLines,
  undoWorkingLine,
  updateWorkingLinesPointsByIndex,
} from '../store'

let lastTouchPoint: PointArray | undefined
let lastTouchClient: { x: number; y: number } | undefined
const zeroPlaces = MAX_EDITOR_LAYERS.toString().length

export default function useMainCanvas(): CanvasConfig {
  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Context
  const { selectedTool, collidingShapeIds } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const shapes = useAppSelector((state) => state.shapes.shapes)

  const isAdjustingUnlabeledColors = useAppSelector(selectIsAdjustingUnlabeledColors)
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const isGroupColoring = useAppSelector(selectisGroupColoring)
  const rebarDisplaySettings = useAppSelector((state) => state.editorSettings.display.rebar)
  const unlabeledColor = useAppSelector((state) => state.editorSettings.display.general.unlabeled_color)
  const currentAdjustingKey = useToolUtilSelector((state) => state.currentAdjustingKey)

  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const workingElementId = useToolUtilSelector((state) => state.workingElementId)

  const inspectionGroupsPanelItems = useAppSelector((state) => state.inspectionGroup.groups)
  const workingLines = useAppSelector((state) => state.toolRebarDetection.workingLines)
  const baseDiameter = useAppSelector((state) => state.toolRebarDetection.baseDiameter)
  const isSaving = useAppSelector((state) => state.toolRebarDetection.isSaving)

  // Refs
  const firstPointPlacedRef = useRef(false)

  // Derived
  const isSelectedTool = useMemo(() => selectedTool === EDITOR_TOOLS.CYLINDER, [selectedTool])
  const volumes = useMemo(() => getVolumeEstimationItems(inspectionItems), [inspectionItems])
  const inspectionGroups = useMemo(() => toInspectionGroups(inspectionGroupsPanelItems), [inspectionGroupsPanelItems])

  /**
   * Get bounding box of volumes
   */
  const volumeBbox: { [name: string]: Box3 } = useMemo(
    () =>
      volumes.reduce((prev, volume) => {
        const bbox = getVolumeBoundingBox(
          shapes.polygons?.filter((shape) => volume.shape_ids?.polygons?.includes(shape.shape_id)) as [
            Polygon,
            Polygon,
          ],
        )

        return {
          ...prev,
          [volume.inspection_item_id!]: bbox,
        }
      }, {}),
    [volumes, shapes],
  )

  /**
   * Mouse click will either add a new distance label (cylinder marker) or finish the current working distance label.
   */
  const onMouseUp = useCallback(
    (point: PointArray | undefined, { isDragged }: CanvasEventsExtra) => {
      // If the user has been dragging the canvas, don't add a new distance label.
      if ((!isSelectedTool && selectedTool !== EDITOR_TOOLS.MOVE) || !point || isDragged || isSaving) return

      if (firstPointPlacedRef.current) {
        dispatch(completeLastWorkingLines(point))
        dispatch(setWorkingElementId(undefined))
        firstPointPlacedRef.current = false
        return
      }

      // Show toast if maximum number of cylinders is reached
      if (workingLines.length >= MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER) {
        if (!toastIdRef.current) {
          toastIdRef.current = toaster.create({
            title: `同時に検出できる要素は${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER}個までです。`,
            description: `右側の「検出中の要素」パネルから削除して${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER}個以下にしてください。`,
            type: 'warning',
            duration: Infinity,
          })
        }
        return
      }

      firstPointPlacedRef.current = true
      const id = crypto.randomUUID()
      dispatch(
        addWorkingLines({
          id,
          diameter: baseDiameter,
          points: [point],
        }),
      )
      dispatch(setWorkingElementId(id))
    },
    [selectedTool, isSelectedTool, isSaving, baseDiameter, toaster, workingLines.length, dispatch],
  )

  /**
   * Undo the last point placed in the working distance label.
   */
  const handleUndo = useCallback(() => {
    if (!isSelectedTool) return

    dispatch(undoWorkingLine())

    const lastWorkingLine = workingLines[workingLines.length - 1]
    const isReopening = lastWorkingLine?.points.length === 2
    firstPointPlacedRef.current = isReopening
    if (isReopening) {
      dispatch(setWorkingElementId(lastWorkingLine.id))
    }
  }, [isSelectedTool, workingLines, dispatch])

  /**
   * When user removes a working distance label, remove the toast if available.
   */
  useEffect(() => {
    if ((workingLines.length < MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER || isSaving) && toastIdRef.current) {
      toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = undefined
    }
  }, [workingLines.length, isSaving])

  /**
   * Reset states when moving out of the tool.
   */
  useEffect(() => {
    const handle = (ev: Event) => {
      const event = ev as CustomEvent<{ newTool: string; prevSelectedTool: string }>
      if ('prevSelectedTool' in event.detail && event.detail.prevSelectedTool === EDITOR_TOOLS.CYLINDER) {
        dispatch(setWorkingElementId(undefined))
        firstPointPlacedRef.current = false
      }
    }
    document.addEventListener('editor:tool:changeReset', handle)
    return () => {
      document.removeEventListener('editor:tool:changeReset', handle)
    }
  }, [dispatch])

  /**
   * Event-specific cleanup
   */
  useEffect(() => {
    const handle = () => {
      dispatch(setWorkingElementId(undefined))
      firstPointPlacedRef.current = false
    }
    document.addEventListener('editor:tool:rebarDetection:delete', handle)
    document.addEventListener('editor:tool:rebarDetection:reset', handle)
    return () => {
      handle()
      document.removeEventListener('editor:tool:rebarDetection:delete', handle)
      document.removeEventListener('editor:tool:rebarDetection:reset', handle)
    }
  }, [dispatch])

  return {
    objects: {
      cylinderMeshes: useMemo(
        (): CylinderMeshProps[] =>
          shapes.cylinders?.map<CylinderMeshProps>((cylinder, index) => {
            const isHovered = isElementHovered(cylinder, hoveredElementId, inspectionGroups)
            const isSelected = selectedElementIds.includes(cylinder.shape_id)
            const isColliding = collidingShapeIds.includes(cylinder.shape_id)
            const surfaceArea = calculateRebarSurfaceArea(cylinder)
            let color = isGroupColoring ? unlabeledColor : rebarDisplaySettings.color
            let outline: CylinderMeshProps['outline'] = {
              color: 'black',
              thickness: 0.001,
            }

            // Check is contained in a volume
            let volumeItem: InspectionItem | undefined
            if (volumes.length > 0) {
              for (let i = 0; i < volumes.length; i += 1) {
                const volume = volumes[i]
                if (isCylinderWithinBoundingBox(cylinder, volumeBbox[volume.inspection_item_id!])) {
                  volumeItem = volume
                  break
                }
              }
            }

            // Cylinder's color is determined by the inspection group it belongs to, if any.
            let group = inspectionGroups.find((grp) => cylinder?.inspection_group_id === grp.group_id)

            // If itself does not belong to any group, check if it belongs to a volume.
            if (!group && volumeItem) {
              group = inspectionGroups.find((grp) => volumeItem?.inspection_group_id === grp.group_id)
            }

            if (group) {
              color = group.color || color
              outline.color = getContrastingOutlineColor(color)
            }

            // Add outline color based on hover or selection state
            if (isHovered) {
              outline = {
                color: 'yellow',
              }
            } else if (isSelected) {
              outline = {
                color: 'white',
              }
            }

            return {
              id: cylinder.shape_id,
              cylinder,
              label:
                rebarDisplaySettings.primary.label.visible && (isHovered || isSelected)
                  ? {
                      label: (
                        <HStack>
                          {isColliding ? <WarningTwoIcon color="orange" /> : null}
                          <Text>
                            鉄筋{zeroPad(index + 1, zeroPlaces)}: {surfaceArea.toFixed(4)}m&sup2;
                          </Text>
                        </HStack>
                      ),
                      styles: {
                        color: rebarDisplaySettings.primary.label.text_color,
                        backgroundColor: rebarDisplaySettings.primary.label.bg_color,
                      },
                      isAdjustingColors:
                        isAdjustingLabelColors &&
                        currentAdjustingKey !== undefined &&
                        ['rebar.primary.label.text_color', 'rebar.primary.label.bg_color'].includes(
                          currentAdjustingKey,
                        ),
                    }
                  : undefined,
              color,
              outline,
              visible: !hiddenElementIds.includes(cylinder.shape_id) || isHovered,
              opacity: hiddenElementIds.includes(cylinder.shape_id) && isHovered ? 0.7 : 1,
              volumeId: volumeItem?.inspection_item_id,
              isAdjustingColors:
                (isAdjustingUnlabeledColors && isGroupColoring) ||
                (!isGroupColoring &&
                  currentAdjustingKey !== undefined &&
                  [`rebar.color`].includes(currentAdjustingKey)),
            }
          }) || [],
        [
          shapes.cylinders,
          inspectionGroups,
          volumes,
          volumeBbox,
          collidingShapeIds,
          selectedElementIds,
          hiddenElementIds,
          hoveredElementId,
          currentAdjustingKey,
          isGroupColoring,
          isAdjustingUnlabeledColors,
          isAdjustingLabelColors,
          rebarDisplaySettings,
          unlabeledColor,
        ],
      ),

      // Working lines
      polylines: workingLines
        .filter((label) => !hiddenElementIds.includes(label.id))
        .map<DistanceLabelWorkingCylinderProps>((label, labelIndex) => {
          const isSelected = selectedElementIds.includes(label.id)
          const isWorking = workingElementId === label.id
          const isEditing = editingElementId === label.id

          return {
            ...label,
            lineColor: isSelected ? 'yellow' : 'white',
            isEditing,
            isWorking,
            line: {
              style: LineStyle.Dashed,
              color: 'red',
            },
            lineOutline: {
              thickness: 0.00075,
            },
            label: {
              label:
                label.points.length === 2
                  ? getDistanceLabel(new Vector3(...label.points[0]).distanceTo(new Vector3(...label.points[1])))
                  : '',
              generateLabel: (points: PointArray[]) =>
                points.length === 2
                  ? getDistanceLabel(new Vector3(...points[0]).distanceTo(new Vector3(...points[1])))
                  : '',
            },
            hideAnchors: true,
            isInteractable: false,
            vertexAnchors: {
              isInteractable: true,
              visible: true,
              depthTest: false,
              depthWrite: false,
              icon: CircleAnchorIcon.MOVE,
              iconColor: 'white',
              outlineOpacity: 0,
              iconScale: 1.4,
              scale: 1.3,
              hoverScale: 1.5,
              color: isSelected ? AnchorColors.Selected : AnchorColors.Movable,
              tooltipText: !isWorking && !isEditing ? '点を移動' : undefined,
              onUpdated: !isWorking
                ? (points) => {
                    dispatch(updateWorkingLinesPointsByIndex({ index: labelIndex, points }))
                  }
                : undefined,
            },
          }
        }),
    },
    events: {
      onTouchStart: (ev, point) => {
        lastTouchPoint = point
        lastTouchClient = { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
      },
      onTouchEnd: (ev, extra) => {
        if (!isSelectedTool) return

        // Last touch point and last touch client are obtained from touchstart and touchstart because touchmove does not have `touches` data.
        if (lastTouchPoint && lastTouchClient) {
          onMouseUp(lastTouchPoint, extra)
        }

        lastTouchPoint = undefined
      },
      onMouseUp: (ev, point, solidMeshPoint, extra) => {
        if (!isSelectedTool || lastTouchClient) {
          lastTouchClient = undefined
          return
        }

        onMouseUp(point || solidMeshPoint, extra)
      },
      onUndo: handleUndo,
    },
  }
}
